import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";

const initialForm = {
  name: "",
};

const SuperAdminReportTypes = () => {
  const token = localStorage.getItem("token");
  const [reportTypes, setReportTypes] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [editingId, setEditingId] = useState("");
  const [editName, setEditName] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState("");

  const authConfig = useMemo(
    () => ({
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }),
    [token]
  );

  const fetchReportTypes = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/super-admin/report-types`,
        authConfig
      );
      setReportTypes(res.data?.reportTypes || []);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Unable to load report types");
    } finally {
      setLoading(false);
    }
  }, [authConfig]);

  useEffect(() => {
    fetchReportTypes();
  }, [fetchReportTypes]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!form.name.trim()) {
      toast.error("Report type name is required");
      return;
    }

    try {
      setSaving(true);
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/super-admin/report-types`,
        { name: form.name },
        authConfig
      );
      setReportTypes((prev) => [...prev, res.data.reportType]);
      setForm(initialForm);
      toast.success(res.data?.message || "Report type added successfully");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Unable to add report type");
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (reportType) => {
    setEditingId(reportType._id);
    setEditName(reportType.name || "");
  };

  const cancelEdit = () => {
    setEditingId("");
    setEditName("");
  };

  const saveEdit = async (id) => {
    if (!editName.trim()) {
      toast.error("Report type name is required");
      return;
    }

    try {
      setSaving(true);
      const res = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/super-admin/report-types/${id}`,
        { name: editName },
        authConfig
      );
      setReportTypes((prev) =>
        prev.map((item) => (item._id === id ? res.data.reportType : item))
      );
      cancelEdit();
      toast.success(res.data?.message || "Report type updated successfully");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Unable to update report type");
    } finally {
      setSaving(false);
    }
  };

  const deleteReportType = async (reportType) => {
    const confirmed = window.confirm(
      `Delete report type "${reportType.name}"?`
    );

    if (!confirmed) {
      return;
    }

    try {
      setDeletingId(reportType._id);
      const res = await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/super-admin/report-types/${reportType._id}`,
        authConfig
      );
      setReportTypes((prev) => prev.filter((item) => item._id !== reportType._id));
      toast.success(res.data?.message || "Report type deleted successfully");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Unable to delete report type");
    } finally {
      setDeletingId("");
    }
  };

  return (
    <div className="container-fluid py-4">
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
        <div>
          <h2 className="mb-1">Report Types</h2>
          <p className="text-muted mb-0">
            Add, edit, and delete compliance report names shown across the platform.
          </p>
        </div>
        <div className="d-flex gap-2 flex-wrap">
          <Link to="/superadmin-form-types" className="btn btn-outline-secondary">
            Form Types
          </Link>
          <Link to="/superadmin-dashboard" className="btn btn-outline-primary">
            Back To Dashboard
          </Link>
        </div>
      </div>

      <div className="row g-4">
        <div className="col-12 col-xl-4">
          <div className="card shadow-sm border-0">
            <div className="card-body">
              <h4 className="mb-3">Add Report Type</h4>
              <form onSubmit={handleSubmit}>
                <label className="form-label">Report Type Name</label>
                <textarea
                  className="form-control"
                  rows="4"
                  value={form.name}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, name: event.target.value }))
                  }
                  disabled={saving}
                  placeholder="Enter report type"
                  required
                />
                <button
                  type="submit"
                  className="btn btn-primary w-100 mt-3"
                  disabled={saving}
                >
                  {saving ? "Saving..." : "Add Report Type"}
                </button>
              </form>
            </div>
          </div>
        </div>

        <div className="col-12 col-xl-8">
          <div className="card shadow-sm border-0">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <h4 className="mb-1">Report Type List</h4>
                  <p className="text-muted mb-0">
                    {loading ? "Loading..." : `${reportTypes.length} report types`}
                  </p>
                </div>
              </div>

              <div className="table-responsive">
                <table className="table table-striped table-hover align-middle">
                  <thead>
                    <tr>
                      <th style={{ width: "80px" }}>#</th>
                      <th>Report Type</th>
                      <th className="text-end" style={{ width: "190px" }}>
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {!loading && reportTypes.length > 0 ? (
                      reportTypes.map((reportType, index) => (
                        <tr key={reportType._id}>
                          <td>{index + 1}</td>
                          <td>
                            {editingId === reportType._id ? (
                              <input
                                className="form-control"
                                value={editName}
                                onChange={(event) => setEditName(event.target.value)}
                                disabled={saving}
                                autoFocus
                              />
                            ) : (
                              reportType.name
                            )}
                          </td>
                          <td className="text-end">
                            {editingId === reportType._id ? (
                              <div className="d-flex justify-content-end gap-2">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-success"
                                  onClick={() => saveEdit(reportType._id)}
                                  disabled={saving}
                                >
                                  Save
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-secondary"
                                  onClick={cancelEdit}
                                  disabled={saving}
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <div className="d-flex justify-content-end gap-2">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={() => startEdit(reportType)}
                                  disabled={Boolean(deletingId)}
                                >
                                  Edit
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => deleteReportType(reportType)}
                                  disabled={deletingId === reportType._id}
                                >
                                  {deletingId === reportType._id ? "Deleting..." : "Delete"}
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="3" className="text-center text-muted py-4">
                          {loading ? "Loading report types..." : "No report types available."}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminReportTypes;
